import { Component, createEffect, onCleanup, onMount, For, Show } from "solid-js";
import { createStore } from "solid-js/store";
import toast from "solid-toast";
import { fetchGet } from "../library/common";
import { ArtworkGridItem } from "./artwork-grid-item";
import { PageList } from "./page-list";
import { AlphabeticalList } from "./form-controls";
import { SalonSubmissionBaseResultItem } from "../Types";

export interface ISearchInstance {
    clear: () => Promise<void>;
    search: (filter: SearchFilter) => Promise<void>;
}
export enum SearchEndpoint {
    Category,
    Default
}
export type SearchSettings = {
    ShowArtist: boolean;
    ShowArtistAlphabetListing?: boolean;
    ShowPageNumbers?: boolean;
    ShowPeoplesChoiceButton?: boolean;
    Endpoint?: SearchEndpoint;
    IsLoggedIn?: boolean;
}
export type SearchHandlers = {
    onPeoplesChoiceVoteUpdated: (count: number) => void;
}
export type SearchArtworksProps = {
    filter: SearchFilter;
    settings?: SearchSettings;
    handlers?: SearchHandlers;
    setInstance?: (instance: ISearchInstance) => void;
}
export type SearchFilter = {
    SalonId: string;
    EntrantType: string;
    ContestEntrantId: string;
    FirstName: string;
    LastName: string;
    Title: string;
    Medium: string;
    ShowMemberVotes?: boolean;
    CategoryId: string;
    AwardId: string;
    StartsWith: string;

    Page: number;
    PageRows: number;
    Order: string;
    Direction: string;
}
type Search = {
    Page: number;
    MaxPage: number;
    Count: number;
    Results: SalonSubmissionBaseResultItem[];
    SurnameLetters: string[];
}
type State = {
    searching: boolean,
}

export const SearchArtworks : Component<SearchArtworksProps> = (props) => {
    const [state, setState] = createStore<State>({
        searching: false
    });
    const [search, setSearch] = createStore<Search>({
        Count: 0,
        Page: 1,
        MaxPage: 1,
        Results: [],
        SurnameLetters: []
    });
    const [settings, setSettings] = createStore<SearchSettings>({
        ShowArtist: props.settings?.ShowArtist,
        Endpoint: props.settings?.Endpoint || SearchEndpoint.Default,
        ShowPageNumbers: props.settings?.ShowPageNumbers,
        ShowArtistAlphabetListing: props.settings?.ShowArtistAlphabetListing,
        ShowPeoplesChoiceButton: props.settings?.ShowPeoplesChoiceButton,
        IsLoggedIn: props.settings?.IsLoggedIn
    } as SearchSettings);
    const [filter, setFilter] = createStore<SearchFilter>({
        SalonId: props.filter.SalonId,
        ContestEntrantId: props.filter.ContestEntrantId,
        CategoryId: props.filter.CategoryId,
        Title: props.filter.Title,
        FirstName: props.filter.FirstName,
        LastName: props.filter.LastName,
        Medium: props.filter.Medium,
        EntrantType: props.filter.EntrantType,
        StartsWith: props.filter.StartsWith,
        Page: 1,
        PageRows: 10
    } as SearchFilter);
    const loadNextPage = async () => {
        if (filter.Page >= search.MaxPage) return;
        setFilter("Page", filter.Page + 1);
        await searchItems();
    };
    const handleScroll = async () => {
        if (window.innerHeight + window.scrollY < document.body.offsetHeight - 500) {
            return;
        }
        if (state.searching) {
            return;
        }
        await loadNextPage();
    };
    createEffect(() => window.addEventListener('scroll', handleScroll));
    onCleanup(() => window.removeEventListener('scroll', handleScroll));
    const searchItems = async () => {
        setState({ searching: true });
        const endpoint = props.settings?.Endpoint == SearchEndpoint.Category ? "/Salon2011/Category/SolidSearchJson" : "/Salon2011/Home/SolidSearchJson";
        const res = await fetchGet(endpoint, filter);
        const json = await res.json();
        if (!json.Success) {
            setState({ searching: false });
            return toast.error("No artworks found.");
        }
        const results = search.Results.concat(json.Data.Items);
        setSearch("Results", results);
        setSearch("MaxPage", json.Data.MaxPage);
        setSearch("SurnameLetters", json.Data.SurnameLetters || []);
        setState({ searching: false });
    };
    if (props.setInstance) {
        props.setInstance({
            clear: async () => {
                setFilter({
                    Page: 1,
                    FirstName: "",
                    LastName: "",
                    Title: "",
                    Medium: "",
                    StartsWith: "",
                    AwardId:"",
                    CategoryId: "",
                    ShowMemberVotes: false,
                    Order: "ByArtist",
                    Direction: "Ascending"
                });
                setSearch("Results", []);
                setSearch("Page", 1);
                setSearch("MaxPage", 1);
                setSearch("Count", 0);
                await searchItems();
            },
            search: async (_filter: SearchFilter) => {
                setSearch("Results", []);
                setSearch("Page", 1);
                setSearch("MaxPage", 1);
                setSearch("Count", 0);
                setFilter({..._filter, Page: 1 });
                await searchItems();
            }
        });
    }
    const updatePageNumber = async (p: number) => {
        setSearch("Results", []);
        setSearch("Page", p);
        setFilter("Page", p);
        await searchItems();
    };
    const updateLetter = async (letter: string) => {
        setSearch("Results", []);
        setSearch("Count", 0);
        setSearch("MaxPage", 1);
        setSearch("Page", 1);
        setFilter("Page", 1);
        if (letter == filter.StartsWith) {
            setFilter("StartsWith", "");
        } else {
            setFilter("StartsWith", letter);    
        }
        await searchItems();
    };
    onMount(async () => {
        await searchItems();
    });
    return (
        <>
            <div style="background-color: #333; position: relative;">
                <div class="finalist-grid">
                    <Show when={settings.ShowPageNumbers}>
                        <div class="theme-dark">
                            <PageList
                                page={search.Page}
                                blockSize={20}
                                maxPage={search.MaxPage}
                                onClick={updatePageNumber}
                            />
                        </div>
                    </Show>
                    <Show when={settings.ShowArtistAlphabetListing}>
                        <div class="theme-dark">
                            <AlphabeticalList
                                letter={filter.StartsWith}
                                existingLetters={search.SurnameLetters}
                                onClick={updateLetter}
                            />
                        </div>
                    </Show>
                    <div style="background-color: #333; position: relative; ">
                        <div class="finalist-grid" style="text-align: center;">
                            <For each={search.Results}>{(item) =>
                                <ArtworkGridItem
                                    item={item}
                                    settings={settings}
                                    itemWidth={350}
                                />
                            }</For>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}